import React, { useRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import Label from '@pixum/label';
import { Icon } from '@pixum/icon';
import type { CommentProps } from './types';
import './styles.css';

const MAX_HEIGHT = 110;

export function Comment({ comment, showMoreLabel, showLessLabel }: CommentProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [showToggle, setShowToggle] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setShowToggle(textRef.current.scrollHeight > MAX_HEIGHT);
    }
  }, [comment]);

  useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setShowToggle(textRef.current.scrollHeight > MAX_HEIGHT);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggle = () => setExpanded(e => !e);

  const handleToggleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <>
      <div
        className={clsx('comment', { 'comment--expanded': expanded })}
        style={{ '--comment-max-height': `${MAX_HEIGHT}px` } as React.CSSProperties}
      >
        <p className="comment__text text--caption" ref={textRef}>
          {comment}
        </p>
      </div>
      {showToggle && (
        <div
          className="comment__toggle"
          tabIndex={0}
          role="button"
          aria-label={expanded ? showLessLabel : showMoreLabel}
          aria-expanded={expanded}
          onClick={handleToggle}
          onKeyDown={handleToggleKeyDown}
        >
          <Label
            className="comment__toggle__label"
            title={expanded ? showLessLabel : showMoreLabel}
            text={expanded ? showLessLabel : showMoreLabel}
            iconPosition="right"
            icon={
              <Icon
                className="comment__toggle__icon"
                name={expanded ? 'chevron-up' : 'chevron-down'}
                loading="lazy"
                size="small"
              />
            }
          />
        </div>
      )}
    </>
  );
}
